import { memo, useEffect, useRef, useState } from 'react'
import { FolderIcon } from '@phosphor-icons/react/dist/ssr/Folder'
import { CustomCellRendererProps } from 'ag-grid-react'

type GroupItem = { label?: string; value?: string; type?: string }
type LeafData = { groups?: GroupItem[] }

const resolveCurrentGroup = (
  params: CustomCellRendererProps,
): GroupItem | undefined => {
  const firstLeaf = params.node?.allLeafChildren?.[0]
  const firstLeafGroups = (firstLeaf?.data as LeafData | undefined)?.groups
  const level = typeof params.node?.level === 'number' ? params.node.level : 0

  if (!Array.isArray(firstLeafGroups) || firstLeafGroups.length === 0) {
    return undefined
  }

  return firstLeafGroups[level] ?? firstLeafGroups[0]
}

const resolveGroupName = (params: CustomCellRendererProps): string => {
  const currentGroup = resolveCurrentGroup(params)

  if (
    typeof currentGroup?.label === 'string' &&
    currentGroup.label.length > 0
  ) {
    return currentGroup.label
  }

  if (
    typeof params.valueFormatted === 'string' &&
    params.valueFormatted.length > 0
  ) {
    return params.valueFormatted
  }

  if (
    typeof currentGroup?.value === 'string' &&
    currentGroup.value.length > 0
  ) {
    return currentGroup.value
  }

  if (typeof params.value === 'string' && params.value.length > 0) {
    return params.value
  }

  if (typeof params.node?.key === 'string' && params.node.key.length > 0) {
    return params.node.key
  }

  return ''
}

export const FolderCellRenderer = memo((params: CustomCellRendererProps) => {
  const { node, api } = params
  const groupName = resolveGroupName(params)

  const [isEditing, setIsEditing] = useState(false)
  const [inputValue, setInputValue] = useState(groupName)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    setInputValue(groupName)
  }, [groupName])

  useEffect(() => {
    if (isEditing) {
      api.deselectAll()
      api.clearCellSelection()
      api.clearFocusedCell()
      inputRef?.current?.focus()
      inputRef?.current?.select()
    }
  }, [isEditing, api])

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      updateProduct()
    }
    if (e.key === 'Escape') {
      setIsEditing(false)
    }
  }

  const handleEditing = () => {
    setIsEditing(true)
  }

  const updateProduct = () => {
    const newValue = inputRef?.current?.value
    if (!newValue || !node) return

    const level = typeof node.level === 'number' ? node.level : 0
    const leafChildren = Array.isArray(node.allLeafChildren)
      ? node.allLeafChildren
      : []

    leafChildren.forEach((leafNode) => {
      const leafData = leafNode.data
      const currentGroups = Array.isArray(leafData?.groups)
        ? leafData.groups
        : []
      if (currentGroups.length === 0) return

      const newGroups = currentGroups.map((group, index) =>
        index === level ? { ...group, label: newValue } : group,
      )
      leafNode.setDataValue('groups', newGroups)
    })

    api.refreshCells({ rowNodes: [node], force: true })
    setIsEditing(false)
  }
  return (
    <div className="flex shrink-0 items-center space-x-2">
      <FolderIcon className="size-4 shrink-0 text-slate-300" weight="fill" />
      {isEditing ? (
        <input
          ref={inputRef}
          name={node?.key as string}
          type="text"
          value={inputValue || ''}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
          onBlur={updateProduct}
          className="focus-visible:ring-ring relative block w-full rounded-sm border-white px-1 text-sm focus:border-white focus:ring-transparent focus:ring-offset-2 focus-visible:outline-none"
        />
      ) : (
        <span
          className="w-full cursor-pointer pr-2 after:mx-2 after:hidden after:content-['✏️'] hover:pr-0 hover:after:inline-block"
          onClick={handleEditing}
          onKeyDown={handleEditing}
        >
          {groupName}
        </span>
      )}
    </div>
  )
})

FolderCellRenderer.displayName = 'FolderCellRenderer'
